import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";

import {
  adminNotificationsList,
  adminNotificationsReadAllCreate,
  adminNotificationsReadCreate,
  adminNotificationsUnreadCountRetrieve,
  storeNotificationsList,
  storeNotificationsReadAllCreate,
  storeNotificationsReadCreate,
  storeNotificationsUnreadCountRetrieve,
  vendorNotificationsList,
  vendorNotificationsReadAllCreate,
  vendorNotificationsReadCreate,
  vendorNotificationsUnreadCountRetrieve,
} from "@motolynk/api-client";
import { usePanel, type Panel } from "@motolynk/app-core";
import { useTranslation } from "@motolynk/i18n";
import { NotificationsMenu, type NotificationMenuItem } from "@motolynk/ui";

import { timeAgo } from "../timeAgo";

export const NOTIFICATION_API = {
  admin: {
    list: adminNotificationsList,
    count: adminNotificationsUnreadCountRetrieve,
    read: adminNotificationsReadCreate,
    readAll: adminNotificationsReadAllCreate,
  },
  vendor: {
    list: vendorNotificationsList,
    count: vendorNotificationsUnreadCountRetrieve,
    read: vendorNotificationsReadCreate,
    readAll: vendorNotificationsReadAllCreate,
  },
  store: {
    list: storeNotificationsList,
    count: storeNotificationsUnreadCountRetrieve,
    read: storeNotificationsReadCreate,
    readAll: storeNotificationsReadAllCreate,
  },
} satisfies Record<Panel, unknown>;

/** How often the unread badge is refreshed (it also refreshes when the tab gets focus). */
export const POLL_MS = 60_000;

export const notificationKeys = {
  all: (panel: Panel) => ["notifications", panel] as const,
  count: (panel: Panel) => ["notifications", panel, "count"] as const,
  list: (panel: Panel) => ["notifications", panel, "list"] as const,
};

/**
 * Open a notification's link: inside this panel (a path, or an absolute URL on this panel's own address) without
 * reloading; anything else (a courier's tracking page) in a new tab.
 */
export function openLink(url: string, go: (path: string) => void): void {
  if (url.startsWith("/")) {
    go(url);
    return;
  }
  try {
    const target = new URL(url);
    if (target.origin === window.location.origin) go(`${target.pathname}${target.search}`);
    else if (target.protocol === "https:" || target.protocol === "http:")
      window.open(target.href, "_blank", "noopener");
  } catch {
    // not a URL: nothing to open
  }
}

/** The bell in the panel's top bar: unread badge, latest 20 notifications, mark read. */
export function NotificationsBell() {
  const { t, i18n } = useTranslation("notifications");
  const { panel } = usePanel();
  const api = NOTIFICATION_API[panel];
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const count = useQuery({
    queryKey: notificationKeys.count(panel),
    queryFn: async () => (await api.count()).data.count,
    refetchInterval: POLL_MS,
  });
  const list = useQuery({
    queryKey: notificationKeys.list(panel),
    queryFn: async () => (await api.list()).data.results,
    enabled: false, // loaded when the menu opens
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: notificationKeys.all(panel) });
  const markRead = useMutation({ mutationFn: (uid: string) => api.read(uid), onSettled: refresh });
  const markAll = useMutation({ mutationFn: () => api.readAll(), onSettled: refresh });

  const items: NotificationMenuItem[] = (list.data ?? []).map((n) => ({
    id: n.uid,
    title: n.title,
    body: n.body,
    time: timeAgo(n.created_at, i18n.language),
    unread: !n.is_read,
  }));
  const byId = new Map((list.data ?? []).map((n) => [n.uid, n]));

  return (
    <NotificationsMenu
      count={count.data ?? 0}
      items={items}
      status={list.isError ? "error" : list.isPending ? "loading" : "ready"}
      onOpenChange={(open) => {
        if (open) void list.refetch();
      }}
      onSelect={(item) => {
        if (item.unread) markRead.mutate(item.id);
        const url = byId.get(item.id)?.url;
        if (url) openLink(url, (path) => void navigate(path));
      }}
      onMarkAllRead={() => markAll.mutate()}
      labels={{
        title: t("title"),
        empty: t("empty"),
        error: t("error"),
        markAllRead: t("markAllRead"),
        unread: t("unread"),
      }}
    />
  );
}
